import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { openDialog } from '../../../base/dialog/actions';
import { isIosMobileBrowser } from '../../../base/environment/utils';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconEnterFullscreen, IconExitFullscreen, IconGear, IconModerator, IconSidePanelDock, IconTileView, IconUsers } from '../../../base/icons/svg';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { open as openParticipantsPane } from '../../../participants-pane/actions.web';
import { openSettingsDialog } from '../../../settings/actions.web';
import { toggleDockedSidePanel } from '../../../side-panel/actions';
import { isSidePanelOpen } from '../../../side-panel/functions';
import { setFullScreen } from '../../../toolbox/actions.web';
import { setTileView } from '../../../video-layout/actions.any';
import { shouldDisplayTileView } from '../../../video-layout/functions.any';
import MuteEveryoneDialog from '../../../video-menu/components/web/MuteEveryoneDialog';
import logger from '../../logger';
/** 顶栏工具图标尺寸。 */
const HEADER_TOOL_ICON_SIZE_PX = 14;
const useStyles = makeStyles()(theme => {
    return {
        root: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: '100%',
            flexShrink: 0,
            paddingRight: '8px',
            boxSizing: 'border-box'
        },
        action: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: '28px',
            padding: '0 8px',
            border: 'none',
            borderRadius: '4px',
            background: 'transparent',
            color: theme.palette.conferenceSubjectText || theme.palette.text02,
            cursor: 'pointer',
            ...theme.typography.labelRegular,
            whiteSpace: 'nowrap',
            '&:hover': {
                backgroundColor: theme.palette.ui03
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '1px'
            }
        },
        iconOnly: {
            width: '28px',
            padding: 0,
            justifyContent: 'center'
        },
        menuWrap: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%'
        },
        menu: {
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: '140px',
            padding: '4px',
            backgroundColor: theme.palette.ui01,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            zIndex: 100
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            border: 'none',
            borderRadius: '4px',
            background: 'transparent',
            color: theme.palette.text01,
            cursor: 'pointer',
            ...theme.typography.labelRegular,
            padding: '8px 10px',
            textAlign: 'left',
            '&:hover': {
                backgroundColor: theme.palette.ui03
            }
        },
        menuItemActive: {
            backgroundColor: theme.palette.action02,
            color: theme.palette.action01
        }
    };
});
/**
 * Web 会议顶栏右侧工具：布局 / 主持人工具 / 设置 / 全屏 / 侧边栏。
 *
 * @returns {JSX.Element}
 */
const ConferenceHeaderToolbar = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const rootRef = useRef(null);
    const [openMenu, setOpenMenu] = useState(null);
    const tileViewEnabled = useSelector(shouldDisplayTileView);
    const isModerator = useSelector(isLocalParticipantModerator);
    const fullScreen = useSelector((state) => state['features/toolbox'].fullScreen);
    const sidePanelOpen = useSelector(isSidePanelOpen);
    const hideFullscreen = isIosMobileBrowser();
    const layoutLabel = tileViewEnabled
        ? t('conferenceHeader.layoutTile')
        : t('conferenceHeader.layoutSpeaker');
    useEffect(() => {
        const onMouseDown = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpenMenu(null);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);
    const toggleMenu = useCallback((menu) => {
        setOpenMenu(prev => (prev === menu ? null : menu));
    }, []);
    const onSelectLayout = useCallback((wantTile) => {
        sendAnalytics(createToolbarEvent('header.layout', { tile: wantTile }));
        logger.info(`[会议顶栏] 切换布局 | tile=${wantTile}`);
        dispatch(setTileView(wantTile));
        setOpenMenu(null);
    }, [dispatch]);
    const onOpenParticipants = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.host.participants'));
        logger.info('[会议顶栏] 打开参会者列表');
        dispatch(openParticipantsPane());
        setOpenMenu(null);
    }, [dispatch]);
    const onMuteEveryone = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.host.muteEveryone'));
        logger.info('[会议顶栏] 打开全体静音确认');
        dispatch(openDialog('MuteEveryoneDialog', MuteEveryoneDialog));
        setOpenMenu(null);
    }, [dispatch]);
    const onOpenSettings = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.settings'));
        logger.info('[会议顶栏] 打开设置（与溢出菜单「打开设置」同源）');
        dispatch(openSettingsDialog());
    }, [dispatch]);
    const onToggleFullscreen = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.fullscreen', { enable: !fullScreen }));
        logger.info(`[会议顶栏] 切换全屏 | next=${!fullScreen}`);
        dispatch(setFullScreen(!fullScreen));
    }, [dispatch, fullScreen]);
    const onToggleSidebar = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.sidebar', { open: !sidePanelOpen }));
        logger.info(`[会议顶栏] 切换侧边栏 | next=${!sidePanelOpen}`);
        dispatch(toggleDockedSidePanel());
    }, [dispatch, sidePanelOpen]);
    return (React.createElement("div", { className: classes.root, ref: rootRef },
        React.createElement("div", { className: classes.menuWrap },
            React.createElement("button", { "aria-expanded": openMenu === 'layout', "aria-label": layoutLabel, className: classes.action, onClick: () => toggleMenu('layout'), type: 'button' },
                React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: IconTileView }),
                React.createElement("span", null, layoutLabel),
                React.createElement(Icon, { size: 10, src: IconArrowDown })),
            openMenu === 'layout' && (React.createElement("div", { className: classes.menu, role: 'menu' },
                React.createElement("button", { className: cx(classes.menuItem, tileViewEnabled && classes.menuItemActive), onClick: () => onSelectLayout(true), role: 'menuitem', type: 'button' }, t('conferenceHeader.layoutTile')),
                React.createElement("button", { className: cx(classes.menuItem, !tileViewEnabled && classes.menuItemActive), onClick: () => onSelectLayout(false), role: 'menuitem', type: 'button' }, t('conferenceHeader.layoutSpeaker'))))),
        isModerator && (React.createElement("div", { className: classes.menuWrap },
            React.createElement("button", { "aria-expanded": openMenu === 'host', "aria-label": t('conferenceHeader.hostTools'), className: classes.action, onClick: () => toggleMenu('host'), type: 'button' },
                React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: IconModerator }),
                React.createElement("span", null, t('conferenceHeader.hostTools')),
                React.createElement(Icon, { size: 10, src: IconArrowDown })),
            openMenu === 'host' && (React.createElement("div", { className: classes.menu, role: 'menu' },
                React.createElement("button", { className: classes.menuItem, onClick: onOpenParticipants, role: 'menuitem', type: 'button' },
                    React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: IconUsers }),
                    React.createElement("span", { style: { marginLeft: 6 } }, t('conferenceHeader.hostParticipants'))),
                React.createElement("button", { className: classes.menuItem, onClick: onMuteEveryone, role: 'menuitem', type: 'button' }, t('conferenceHeader.hostMuteEveryone')))))),
        React.createElement("button", { "aria-label": t('conferenceHeader.settings'), className: classes.action, onClick: onOpenSettings, title: t('conferenceHeader.settings'), type: 'button' },
            React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: IconGear }),
            React.createElement("span", null, t('conferenceHeader.settings'))),
        !hideFullscreen && (React.createElement("button", { "aria-label": fullScreen
                ? t('conferenceHeader.exitFullscreen')
                : t('conferenceHeader.enterFullscreen'), className: cx(classes.action, classes.iconOnly), onClick: onToggleFullscreen, title: fullScreen
                ? t('conferenceHeader.exitFullscreen')
                : t('conferenceHeader.enterFullscreen'), type: 'button' },
            React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: fullScreen ? IconExitFullscreen : IconEnterFullscreen }))),
        React.createElement("button", { "aria-label": sidePanelOpen
                ? t('conferenceHeader.hideSidebar')
                : t('conferenceHeader.showSidebar'), "aria-pressed": sidePanelOpen, className: cx(classes.action, classes.iconOnly), onClick: onToggleSidebar, title: sidePanelOpen
                ? t('conferenceHeader.hideSidebar')
                : t('conferenceHeader.showSidebar'), type: 'button' },
            React.createElement(Icon, { size: HEADER_TOOL_ICON_SIZE_PX, src: IconSidePanelDock }))));
};
export default ConferenceHeaderToolbar;
